import type { EarliestConsumptionPerLocationDTO } from '@/src/api/types/procurement'
import { FormSelect } from '@/src/components/Form/FormSelect'
import { ALL_LOCATIONS, type EnrichedLocation, isLocation, useLocationsContext } from '@/src/contexts/LocationsContext'
import {
  useLoadPlanningEarliestConsumption,
  useLoadedStrategyMetadata,
} from '@/src/features/procurement-and-markets/load-planning/hooks/LoadPlanning.hooks'
import { useIsCustomReferenceBaseLoadEnabled } from '@/src/shared/feature-flag/feature-flag.hooks'
import { appColors } from '@/src/theme'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { mapToEnergyTypeDto } from '../../details/types/adjustment.utils'

type Option = { value: number; label: string }

interface LoadPlanningAdjustmentLocationSelectProps {
  value?: number | null
  onChange: (value: number) => void
  isDisabled?: boolean
}

export const LoadPlanningAdjustmentLocationSelect = ({ value, onChange, isDisabled }: LoadPlanningAdjustmentLocationSelectProps) => {
  const { t } = useTranslation()
  const isCustomReferenceBaseLoadEnabled = useIsCustomReferenceBaseLoadEnabled()

  const { data: strategyMetadata } = useLoadedStrategyMetadata()
  const { companyPgId: companyId, allLocations, isInitialized } = useLocationsContext()

  const { data: earliestConsumption } = useLoadPlanningEarliestConsumption({
    companyId: companyId!,
    energyType: mapToEnergyTypeDto(strategyMetadata?.energyType),
    year: strategyMetadata?.year,
  })

  const eligibleLocationIds = useMemo(() => {
    if (!earliestConsumption) return undefined
    return new Set((earliestConsumption as EarliestConsumptionPerLocationDTO[]).map(e => String(e.locationId)))
  }, [earliestConsumption])

  const locationOptions: Option[] = useMemo(() => {
    const companyOption: Option = {
      value: ALL_LOCATIONS,
      label: t('components.procurement.forecasting.modal.form.all_locations_option'),
    }

    const singleLocationOptions = allLocations
      .filter((location): location is EnrichedLocation => isLocation(location))
      .filter(location => !eligibleLocationIds || eligibleLocationIds.has(String(location.locationPgId)))
      .map(location => ({
        value: location.locationPgId,
        label: location.name,
      }))

    return isCustomReferenceBaseLoadEnabled ? [companyOption, ...singleLocationOptions] : singleLocationOptions
  }, [allLocations, eligibleLocationIds, isCustomReferenceBaseLoadEnabled, t])

  return (
    <FormSelect
      isRequired={true}
      name='locationId'
      label={t('components.procurement.forecasting.modal.location')}
      options={locationOptions}
      placeholder={t('components.procurement.forecasting.modal.form.location_placeholder')}
      value={value}
      onChange={onChange}
      labelProps={{
        fontWeight: 86,
        fontSize: '14px',
        color: appColors.secondaryForeground,
      }}
      isDisabled={isDisabled || !isInitialized}
    />
  )
}
